import { Box, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import { PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import {
  LSPageContainer,
  LSVideoPartContainer,
  LSMainContent,
  LSRightContentContainer,
} from '../../LiveSessionPageStyles'
import VideoCallContainer from '../VideoCall/VideoCallContainer'
import SessionTopTabs from '../TopTabs/SessionTopTabs'
import SessionDrawer from '../SessionDrawer/SessionDrawer'
import PrescriptionFooterComponent from 'src/mentor/components/PrescriptionMakingPage/layoutComponents/PrescriptionFooterComponent'
import { HotlineSectionSeparator } from 'src/consumer/components/HotlineChamberPage/HotlineChamberStyles'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import useResizable from 'src/consumer/components/LiveSessionPage/hooks/useResizable'

const SessionContainer = forwardRef(
  (
    {
      sessionStarted,
      menuItemsList,
      selectedMenuItem,
      setSelectedMenuItem,
      renderWhatINeed,
      screenWidth,
      userRole,
      joinAgoraCall,
      agoraRtcProps,
      agoraRtmProps,
      onCameraClose,
      onCameraOpen,
      onAudioClose,
      onAudioOpen,
      handleLeaveRoom,
      handleEndVideoCall,
      showRejoin,
      sessionRoutingDecision,
      drawerForMoreOptionsOpenMobile,
      setDrawerForMoreOptionsOpenMobile,
      chatProps,
      joinAgoraCallFunction,
      timeElapsedInSeconds,
      setTimeElapsedInSeconds,
      leaveRoom,
      hidePreviewButton,
      moveToPrintPrescriptionScreen,
      setMoveToPrintPrescriptionScreen,
      eventType,
      markedCompleted,
      auxiliaryData,
      bookedEventInfo,
      changeLayoutFunction,
      onContentDrawerOpenChange, // Callback to notify parent when drawer state changes
      onOpenDrawerChange, // Callback to expose handleOpenDrawer to parent
      therapyReportRef,
      prescriptionRef,
      screeningReportRef,
      assessmentReportRef,
      // Device permissions for camera preview
      selectedCamera,
      selectedMicrophone,
      userHasAllowedAudio,
      userHasAllowedVideo,
      useAppOwnedRtcSurface,
      microphoneError,
      cameraError,
      setUserHasAllowedAudio,
      setUserHasAllowedVideo,
      setGiveCameraPermissionDialogOpen,
      setGiveMicrophonePermissionDialogOpen,
      meetingSrc,
      // Available devices for settings dropdown
      availableCameras,
      availableMicrophones,
      setSelectedCamera,
      setSelectedMicrophone,
      // Support QR code from API
      supportQrCodeMediaId,
    },
    ref,
  ) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [contentDrawerOpen, setContentDrawerOpen] = useState(false) // Drawer for right content on mobile
    
    // Use ref to store the callback function to avoid infinite loops
    // Initialize ref with the callback (captured on first render only)
    const onContentDrawerOpenChangeRef = React.useRef(onContentDrawerOpenChange)
    const hasExposedCallbackRef = React.useRef(false)
    
    // Expose drawer open function to parent via callback (only once on mount)
    // We use the initial callback value and don't update it to prevent infinite loops
    React.useEffect(() => {
      if (onContentDrawerOpenChangeRef.current && !hasExposedCallbackRef.current) {
        hasExposedCallbackRef.current = true
        // Create a stable function that opens the drawer
        // Wrap state update in setTimeout to ensure it runs after render phase
        const openDrawerFn = () => {
          setTimeout(() => {
            setContentDrawerOpen(true)
          }, 0)
        }
        // Use requestAnimationFrame to defer the callback until after render completes
        // This prevents React warnings about updating components during render
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (onContentDrawerOpenChangeRef.current) {
              onContentDrawerOpenChangeRef.current(openDrawerFn)
            }
          })
        })
      }
      // Only run once on mount - don't include onContentDrawerOpenChange in deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Empty dependency array - only run on mount
    
    // Close drawer when session starts (drawer should be closed by default)
    // Also close drawer when sessionStarted changes from false to true (when joining)
    React.useEffect(() => {
      if (sessionStarted) {
        setContentDrawerOpen(false)
      }
    }, [sessionStarted])
    
    // Ensure drawer is closed on initial mount (mobile drawer should start closed)
    // Removed this useEffect as it conflicts with the callback exposure above
    // The drawer is already initialized to false via useState(false)
    
    const prescriptionViewSettings = useSelector(selectPrescriptionViewSettingsData)
    const prescriptionViewSettingsData = prescriptionViewSettings?.data

    const isTabletView =
      prescriptionViewSettingsData?.prescriptionBuilderViewType ===
      PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView
    const hasVideoCall = menuItemsList?.some((item) => item?.id === 'video_call')

    // Refs for split-pane resize functionality
    const refBoxLeft = useRef()
    const refBoxRight = useRef()
    const refDragger = useRef()

    // Enable split-pane resizing when video call is available
    const { isResizing } = useResizable({
      refDragger,
      refLeftPanel: refBoxLeft,
      refRightPanel: refBoxRight,
      minLeftWidth: 280,
      minRightWidth: 360,
      disabled: !hasVideoCall,
    })

    // Simple drawer state - only changes on explicit user actions (button clicks, close button)
    // NO auto-open or auto-close logic
    const [drawerOpenState, setDrawerOpenStateInternal] = useState(false)
    
    // Wrapper to log all drawer state changes with stack trace
    const setDrawerOpen = React.useCallback((value) => {
      setDrawerOpenStateInternal(value)
    }, [])
    
    const drawerOpen = drawerOpenState
    
    // Get primary section (first non-video_call item) - loaded in main area
    const primarySection = React.useMemo(() => {
      const filteredMenuItems = menuItemsList?.filter((item) => item?.id !== 'video_call') || []
      return filteredMenuItems[0]
    }, [menuItemsList])
    
    // Handler to open drawer when secondary button is clicked - called explicitly from button click
    // This is passed to SessionTopTabs via LiveSessionHeaderComponent
    const handleOpenDrawer = React.useCallback((itemId) => {
      // Assessments uses its own right-side PractitionerAssessmentDrawer; do not open the bottom content drawer
      if (itemId === 'assessments') {
        return
      }
      // Check if it's primary section
      const filteredMenuItems = menuItemsList?.filter((item) => item?.id !== 'video_call') || []
      const primarySectionId = filteredMenuItems[0]?.id
      const isPrimary = primarySectionId === itemId
      
      // Only open drawer if it's NOT the primary section
      if (!isPrimary) {
        setDrawerOpen(true)
      } else {
        setDrawerOpen(false)
      }
    }, [menuItemsList, setDrawerOpen])
    
    // Track previous drawer state to detect when it closes
    const prevDrawerOpenRef = React.useRef(drawerOpen)
    
    // Reset selectedMenuItem to primary section when drawer closes
    // This ensures buttons don't remain highlighted when drawer closes
    React.useEffect(() => {
      const drawerJustClosed = prevDrawerOpenRef.current === true && drawerOpen === false
      const shouldReset = drawerJustClosed && selectedMenuItem && primarySection?.id && selectedMenuItem !== primarySection.id
      
      // Update ref for next render
      prevDrawerOpenRef.current = drawerOpen
      
      if (shouldReset) {
        setSelectedMenuItem(primarySection.id)
      }
    }, [drawerOpen, selectedMenuItem, primarySection, setSelectedMenuItem])
    
    // Expose handleOpenDrawer to parent via callback (similar to onContentDrawerOpenChange)
    // Use ref to track if we've already exposed the handler to prevent multiple calls
    const hasExposedHandlerRef = React.useRef(false)
    const onOpenDrawerChangeRef = React.useRef(onOpenDrawerChange)
    const lastMenuItemsListRef = React.useRef(null)
    
    // Update ref when callback changes
    React.useEffect(() => {
      onOpenDrawerChangeRef.current = onOpenDrawerChange
    }, [onOpenDrawerChange])
    
    // Only expose handler once when it's ready and menuItemsList is available
    // Only re-expose if menuItemsList actually changes (not just reference)
    React.useEffect(() => {
      if (!onOpenDrawerChangeRef.current || !menuItemsList || menuItemsList.length === 0) {
        return
      }
      
      // Check if menuItemsList actually changed (compare IDs)
      const currentMenuIds = menuItemsList.map(item => item.id).join(',')
      const lastMenuIds = lastMenuItemsListRef.current
      const menuItemsChanged = currentMenuIds !== lastMenuIds
      
      // Only expose if we haven't exposed yet, or if menuItemsList actually changed
      if (!hasExposedHandlerRef.current || menuItemsChanged) {
        onOpenDrawerChangeRef.current(handleOpenDrawer)
        hasExposedHandlerRef.current = true
        lastMenuItemsListRef.current = currentMenuIds
      }
    }, [handleOpenDrawer, menuItemsList])
    
    useImperativeHandle(ref, () => ({}))

    // Fix aria-hidden accessibility issue for mobile drawer
    // When drawer is open and has focusable content, ensure backdrop doesn't have aria-hidden
    useEffect(() => {
      if (!contentDrawerOpen || !isMobile) return

      const timer = setTimeout(() => {
        // Find the drawer modal root by searching for the most recently opened drawer
        const drawers = document.querySelectorAll('.MuiDrawer-root.MuiDrawer-anchorBottom')
        const openDrawer = Array.from(drawers).find(drawer => {
          const modalRoot = drawer.closest('.MuiModal-root')
          return modalRoot && modalRoot.style.display !== 'none'
        })
        
        if (openDrawer) {
          const modalRoot = openDrawer.closest('.MuiModal-root')
          if (modalRoot) {
            const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper')
            const hasFocusableContent = drawerPaper?.querySelector(
              'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
            
            if (hasFocusableContent) {
              modalRoot.removeAttribute('aria-hidden')
              const backdrop = modalRoot.querySelector('.MuiBackdrop-root')
              if (backdrop) {
                backdrop.removeAttribute('aria-hidden')
              }
            }
          }
        }
      }, 100)

      return () => {
        clearTimeout(timer)
      }
    }, [contentDrawerOpen, isMobile])

    // Get action buttons based on selected menu item
    const getActionButtons = () => {
      // Only show buttons for primary sections (not secondary like History, Report Preview)
      const currentItem = menuItemsList?.find((item) => item.id === selectedMenuItem)
      const isPrimarySection = currentItem && !currentItem.secondary
      
      if (!isPrimarySection) {
        return { showSaveButtons: false }
      }
      
      if (selectedMenuItem === 'therapy_report' && therapyReportRef?.current) {
        return {
          showSaveButtons: true,
          save: () => therapyReportRef.current?.saveTherapyReport?.(),
          // Print/Save & Print buttons removed from editing page
          isLoading: therapyReportRef.current?.prescriptionSaveLoading || false,
        }
      }
      
      if (selectedMenuItem === 'prescription' && prescriptionRef?.current) {
        return {
          showSaveButtons: true,
          save: () => prescriptionRef.current?.savePrescriptionData?.(),
          saveAndPrint: () => prescriptionRef.current?.saveAndPrint?.(),
          isLoading: prescriptionRef.current?.prescriptionSaveLoading || false,
        }
      }
      
      if (selectedMenuItem === 'screening_report' && screeningReportRef?.current) {
        return {
          showSaveButtons: true,
          save: () => screeningReportRef.current?.saveScreeningReport?.(),
          saveAndPrint: () => screeningReportRef.current?.saveAndPrint?.(),
          isLoading: screeningReportRef.current?.prescriptionSaveLoading || false,
        }
      }
      
      if (selectedMenuItem === 'assessment_report') {
        return { showSaveButtons: false }
      }
      
      return { showSaveButtons: false }
    }

    if (!sessionStarted) {
      return null
    }

    const actionButtons = getActionButtons()
    
    // Get therapy report status for draft badge
    const therapyReportStatus = therapyReportRef?.current?.therapyReportStatus || null
    const handleSettingsClick = () => {
      if (selectedMenuItem === 'therapy_report' && therapyReportRef?.current?.openSettings) {
        therapyReportRef.current.openSettings()
      } else if (selectedMenuItem === 'screening_report' && screeningReportRef?.current?.openSettings) {
        screeningReportRef.current.openSettings()
      } else if (selectedMenuItem === 'assessment_report' && assessmentReportRef?.current?.openSettings) {
        assessmentReportRef.current.openSettings()
      }
    }

    return (
      <>
        {/* Main Content Area */}
        {/* Spacer to account for fixed header + tabs (header can grow with patient details; tabs 48px) */}
        <Box
          sx={{
            height: isMobile ? '164px' : '148px', // header (min 56/64px, can grow) + 48px tabs + buffer so header is never cut by tabs
            width: '100%',
            flexShrink: 0,
          }}
        />
        <LSPageContainer sessionStarted={sessionStarted} style={{ marginTop: '0px', paddingTop: '0px' }}>
          {' '}
          {/* 60px header + 48px tabs */}
          <LSVideoPartContainer
            sessionStarted={sessionStarted}
            style={{
              gap: 0,
              overflow: sessionStarted ? 'hidden' : 'visible',
              overflowX: 'visible',
              overflowY: hasVideoCall && sessionStarted ? 'hidden' : 'auto',
              background: '#FFF',
              paddingTop: '0px',
              marginTop: '0px',
              minHeight: hasVideoCall ? 'unset' : 'auto',
              height: hasVideoCall ? 'calc(100vh - 108px)' : 'auto',
              maxHeight: hasVideoCall ? 'calc(100vh - 108px)' : 'none',
              position: 'relative',
              alignItems: 'flex-start', // Always align to top, never center
              width: '100%',
              maxWidth: '100vw',
            }}
          >
            {hasVideoCall && (
              <div
                ref={refBoxLeft}
                className="resizable-box hide-on-print-only"
                style={{
                  // Mobile: Full width (right content is in drawer). Desktop: 50% split
                  width: isMobile ? '100%' : '50%',
                  height: '100%',
                  maxHeight: '100%', // Ensure it doesn't exceed parent height
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0, // Prevent flexbox from auto-resizing
                  overflow: 'hidden', // Prevent scrolling - controls use absolute positioning
                  position: 'relative', // Ensure proper positioning context for absolute children
                }}
              >
                <VideoCallContainer
                  userRole={userRole}
                  joinAgoraCall={joinAgoraCall}
                  agoraRtcProps={agoraRtcProps}
                  agoraRtmProps={agoraRtmProps}
                  onCameraClose={onCameraClose}
                  onCameraOpen={onCameraOpen}
                  onAudioClose={onAudioClose}
                  onAudioOpen={onAudioOpen}
                  handleLeaveRoom={handleLeaveRoom}
                  handleEndVideoCall={handleEndVideoCall}
                  showRejoin={showRejoin}
                  sessionRoutingDecision={sessionRoutingDecision}
                  screenWidth={screenWidth}
                  menuItemsList={menuItemsList}
                  selectedMenuItem={selectedMenuItem}
                  setSelectedMenuItem={setSelectedMenuItem}
                  renderWhatINeed={renderWhatINeed}
                  drawerForMoreOptionsOpenMobile={drawerForMoreOptionsOpenMobile}
                  setDrawerForMoreOptionsOpenMobile={setDrawerForMoreOptionsOpenMobile}
                  chatProps={chatProps}
                  joinAgoraCallFunction={joinAgoraCallFunction}
                  hasTopTabs={true} // New prop to indicate top tabs layout
                  bookedEventInfo={bookedEventInfo}
                  auxiliaryData={auxiliaryData}
                  peerChatName={chatProps?.peerChatName}
                  peerChatImage={chatProps?.peerChatImage}
                  changeLayoutFunction={changeLayoutFunction}
                  // Device permissions for camera preview
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  userHasAllowedAudio={userHasAllowedAudio}
                  userHasAllowedVideo={userHasAllowedVideo}
                  useAppOwnedRtcSurface={useAppOwnedRtcSurface}
                  microphoneError={microphoneError}
                  cameraError={cameraError}
                  setUserHasAllowedAudio={setUserHasAllowedAudio}
                  setUserHasAllowedVideo={setUserHasAllowedVideo}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                  meetingSrc={meetingSrc}
                  // Available devices for settings dropdown
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  // Support QR code from API
                  supportQrCodeMediaIdFromApi={supportQrCodeMediaId}
                />
              </div>
            )}
            {/* Desktop: Resizer separator - Hidden on mobile (right content is in drawer) */}
            {hasVideoCall && userRole === 'mentor' && !isMobile && (
              <HotlineSectionSeparator
                ref={refDragger}
                className="left-resizer hide-on-print-only"
                style={{ cursor: 'ew-resize' }}
              >
                <DragIndicatorIcon style={{ fontSize: '15px' }} />
              </HotlineSectionSeparator>
            )}
            {/* Desktop: Right content container - Hidden on mobile (shown in drawer) */}
            <LSRightContentContainer 
              ref={refBoxRight} 
              hasVideoCall={hasVideoCall}
              sessionStarted={sessionStarted}
              style={{
                // Mobile: Hide right content only if there's a video call (it's in drawer). 
                // For offline sessions (no video call), show content directly
                display: isMobile && hasVideoCall ? 'none' : undefined,
              }}
            >
              <LSMainContent
                withoutSidebar={true}
                sessionStarted={sessionStarted}
                style={{
                  width: '100%',
                  maxWidth: '100vw',
                  height: hasVideoCall && sessionStarted ? '100%' : 'auto',
                  minHeight: 'auto',
                  overflowX: 'visible',
                  overflowY: 'visible',
                  boxSizing: 'border-box',
                  position: 'relative',
                  // Ensure content starts at top
                  scrollBehavior: 'auto',
                }}
              >
                {/* Always show primary section (Therapy Report) in main content area */}
                {/* Secondary sections (History, Report Preview) only show in drawer */}
                {primarySection?.id && renderWhatINeed(primarySection.id)}
              </LSMainContent>

              {isTabletView && (
                <PrescriptionFooterComponent
                  endConsultation={leaveRoom}
                  timeElapsedInSeconds={timeElapsedInSeconds}
                  setTimeElapsedInSeconds={setTimeElapsedInSeconds}
                  sidebarSelectMenu={menuItemsList}
                  sidebarSelectedMenuItem={selectedMenuItem}
                  setSidebarSelectedMenuItem={(itemId) => {
                    setSelectedMenuItem(itemId)
                  }}
                  onPrescriptionPreviewPage={hidePreviewButton}
                  goToPrintPage={() => {
                    if (selectedMenuItem !== 'prescription') {
                      setSelectedMenuItem('prescription')
                    }
                    setMoveToPrintPrescriptionScreen(true)
                  }}
                />
              )}
            </LSRightContentContainer>
          </LSVideoPartContainer>
        </LSPageContainer>

        {/* Session Drawer for Secondary Sections (History, Vitals History, Rx, etc.) */}
        {/* This drawer opens when any button except the first one (primary section) is clicked */}
        <SessionDrawer
          key={`drawer-${selectedMenuItem}`}
          open={drawerOpen}
              onClose={(event, reason) => {
                // Close drawer and switch back to primary section
                if (reason === 'backdropClick') {
                  const target = event?.target
                  const isClickOnButtons = target?.closest('.session-top-tabs')
                  if (isClickOnButtons) {
                    return // Don't close if clicking on buttons
                  }
                }
                
                // Switch back to primary section FIRST, then close drawer
                // This ensures the button highlight is reset immediately
                if (primarySection?.id) {
                  setSelectedMenuItem(primarySection.id)
                }
                // Then close the drawer
                setDrawerOpen(false)
              }}
          selectedMenuItem={selectedMenuItem}
          menuItemsList={menuItemsList}
          renderWhatINeed={renderWhatINeed}
          chatProps={chatProps}
        />

        {/* Mobile: Right Content Drawer (Therapy Report/Consultation/Prescription) */}
        {isMobile && hasVideoCall && (
          <Drawer
            anchor="bottom"
            open={contentDrawerOpen}
            onClose={() => setContentDrawerOpen(false)}
            ModalProps={{
              disableScrollLock: true,
            }}
            PaperProps={{
              sx: {
                height: '85vh',
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {/* Drawer Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                pb: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                background: 'rgba(255, 255, 255, 0.98)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DescriptionIcon sx={{ fontSize: 24, color: '#6779e3' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  {menuItemsList?.find((item) => item.id === selectedMenuItem)?.label || 
                   menuItemsList?.find((item) => item.id === selectedMenuItem)?.title || 
                   primarySection?.title || 
                   'Session Details'}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setContentDrawerOpen(false)}
                sx={{
                  color: 'rgba(103, 121, 227, 0.8)',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '1px solid rgba(103, 121, 227, 0.2)',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Drawer Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 2,
              }}
            >
              <LSMainContent
                withoutSidebar={true}
                sessionStarted={sessionStarted}
                style={{
                  width: '100%',
                  height: sessionStarted ? '100%' : 'auto',
                  flex: 1,
                }}
              >
                {/* Render content based on selected menu item (primary or secondary) */}
                {renderWhatINeed(selectedMenuItem)}
              </LSMainContent>

              {isTabletView && (
                <PrescriptionFooterComponent
                  endConsultation={leaveRoom}
                  timeElapsedInSeconds={timeElapsedInSeconds}
                  setTimeElapsedInSeconds={setTimeElapsedInSeconds}
                  sidebarSelectMenu={menuItemsList}
                  sidebarSelectedMenuItem={selectedMenuItem}
                  setSidebarSelectedMenuItem={(itemId) => {
                    setSelectedMenuItem(itemId)
                  }}
                  onPrescriptionPreviewPage={hidePreviewButton}
                  goToPrintPage={() => {
                    if (selectedMenuItem !== 'prescription') {
                      setSelectedMenuItem('prescription')
                    }
                    setMoveToPrintPrescriptionScreen(true)
                  }}
                />
              )}
            </Box>
          </Drawer>
        )}
      </>
    )
  },
)

export default SessionContainer
